import React, { useState } from 'react';
import { CNCGroup } from '../../types';

interface SSOMAEventManagerProps {
    groups: CNCGroup[];
    setGroups: React.Dispatch<React.SetStateAction<CNCGroup[]>>;
}

const SSOMAEventManager: React.FC<SSOMAEventManagerProps> = ({ groups = [], setGroups }) => {
    const [newGroupName, setNewGroupName] = useState('');
    const [newCauseName, setNewCauseName] = useState<{ [groupId: string]: string }>({});

    const handleAddGroup = () => {
        if (!newGroupName.trim()) return;
        setGroups([...groups, {
            id: `se-g-${Date.now()}`,
            name: newGroupName.trim(),
            causes: []
        }]);
        setNewGroupName('');
    };

    const handleDeleteGroup = (groupId: string) => {
        if (confirm('¿Está seguro de eliminar esta categoría de Eventos No Deseados?')) {
            setGroups(groups.filter(g => g.id !== groupId));
        }
    };

    const handleAddCause = (groupId: string) => {
        const causeName = newCauseName[groupId];
        if (!causeName || !causeName.trim()) return;
        
        setGroups(groups.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    causes: [...g.causes, { id: `se-c-${Date.now()}`, name: causeName.trim() }]
                };
            }
            return g;
        }));
        
        setNewCauseName(prev => ({ ...prev, [groupId]: '' }));
    };

    const handleDeleteCause = (groupId: string, causeId: string) => {
        setGroups(groups.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    causes: g.causes.filter(c => c.id !== causeId)
                };
            }
            return g;
        }));
    };

    return (
        <div className="p-4 md:p-8 space-y-6">
            <div className="bg-white dark:bg-[#1a2634] p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div>
                    <h2 className="text-xl font-black text-slate-800 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-red-500 text-[24px]">report_problem</span>
                        Maestro de Eventos No Deseados (SSOMA)
                    </h2>
                    <p className="text-sm text-slate-500 mt-2">Cree las categorías y eventos no deseados estándar para clasificar actos o condiciones subestándar e incidentes.</p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-2 mt-6">
                    <input 
                        type="text" 
                        placeholder="Nueva Categoría (Ej: Actos Subestándar)" 
                        className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500 text-slate-800 dark:text-white"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
                    />
                    <button 
                        onClick={handleAddGroup}
                        className="w-full md:w-auto bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow hover:bg-red-600 transition-colors"
                    >
                        Agregar Categoría
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {groups.map(group => (
                    <div key={group.id} className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full">
                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="font-bold text-slate-800 dark:text-white">{group.name}</h3>
                            <button 
                                onClick={() => handleDeleteGroup(group.id)}
                                className="text-slate-400 hover:text-red-500 p-1"
                            >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                            </button>
                        </div>
                        
                        <div className="flex-1 space-y-2 mb-4 overflow-y-auto max-h-48 pr-2">
                            {group.causes.length === 0 ? (
                                <p className="text-xs text-slate-400 text-center py-4 italic">No hay eventos añadidos</p>
                            ) : group.causes.map(cause => (
                                <div key={cause.id} className="flex justify-between items-center bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded-lg group">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{cause.name}</span>
                                    <button 
                                        onClick={() => handleDeleteCause(group.id, cause.id)}
                                        className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">close</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                        
                        <div className="flex gap-2 mt-auto">
                            <input 
                                type="text" 
                                placeholder="Añadir nuevo evento..." 
                                className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-red-500 text-slate-800 dark:text-white"
                                value={newCauseName[group.id] || ''}
                                onChange={(e) => setNewCauseName({...newCauseName, [group.id]: e.target.value})}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddCause(group.id)}
                            />
                            <button 
                                onClick={() => handleAddCause(group.id)}
                                className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
                            >
                                Añadir
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SSOMAEventManager;
